import { Flex } from '@chakra-ui/layout';
import {
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';

import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';

export const TextInput = ({
  name,
  label,
  placeholder = '',
  defaultValue = '',
}: {
  name: string;
  label: string;
  placeholder?: string;
  defaultValue?: string;
}) => {
  const { control } = useFormContext();

  return (
    <FormControl>
      <FormLabel fontWeight="bold">{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <Input type="text" placeholder={placeholder} {...field} />
        )}
      />
    </FormControl>
  );
};

export const DateInput = ({
  name,
  label,
  placeholder = 'Enter Date',
}: {
  name: string;
  label: string;
  placeholder?: string;
}) => {
  const { register, unregister, control, setValue } = useFormContext();
  const selectedDate = useWatch({ control, name });

  useEffect(() => {
    register(name);

    return () => {
      unregister(name);
    };
  }, [name, register, unregister]);

  return (
    <FormLabel>
      <Typography variant="base" fontWeight="bold" TextProps={{ mb: '1rem' }}>
        {label}
      </Typography>
      <SimpleDatePicker
        width="100%"
        placeHolder={placeholder}
        setSelectedDate={(date: Date | null) => {
          if (!date) return;
          setValue(name, date);
        }}
        selectedDate={selectedDate}
      />
    </FormLabel>
  );
};

export const SelectInput = ({
  name,
  label,
  options,
  placeholder = '',
}: {
  name: string;
  label: string;
  options: { label: React.ReactNode; value: string }[];
  placeholder?: string;
}) => {
  const { control, register, unregister, setValue } = useFormContext();
  const value = useWatch({ control, name });

  useEffect(() => {
    register(name);

    return () => {
      unregister(name);
    };
  }, [name, register, unregister]);

  return (
    <FormLabel>
      <Typography variant="base" fontWeight="bold" TextProps={{ mb: '1rem' }}>
        {label}
      </Typography>
      <Select
        placeholderLabel={placeholder}
        value={value}
        onChange={(nextValue) => {
          setValue(name, nextValue);
        }}
        options={options}
      />
    </FormLabel>
  );
};

export const InputContainer = ({
  label,
  children,
  deleteProperty,
}: {
  label: string;
  children: React.ReactNode;
  deleteProperty: () => void;
}) => (
  <Flex direction="row" mb="1.5rem" gap="0.8rem">
    <Flex direction="column" flexGrow={1}>
      <Typography variant="base" fontWeight="bold">
        {label}
      </Typography>
      <Card variant="outline" size="md" mt="0.5rem">
        <CardBody display="flex" flexDirection="column">
          {children}
        </CardBody>
      </Card>
    </Flex>

    <Flex direction="column" flexShrink={0}>
      <IconButtonV2
        aria-label="Delete Property"
        icon={<Icon icon="trash" />}
        onClick={deleteProperty}
        variant="v2-ghost"
      />
    </Flex>
  </Flex>
);
